import React from 'react';

const BG = '#F4F8F9';
const BORDER = '#E5ECEC';
const MUTED = '#6B7787';
const TEAL_DARK = '#05292A';
const CARD_SHADOW = '0 1px 2px rgba(5,41,42,0.04), 0 8px 24px rgba(5,41,42,0.05)';

// Inline styles keep this file identical to the inventory-web copy, which
// cannot rely on Tailwind classes; IconLayers at the bottom is host-only.

/** The white card every report filter bar sits in. Children wrap; on mobile they stack. */
export const FilterBar = ({ children, mobile }: { children: React.ReactNode; mobile?: boolean }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 12,
      borderRadius: 16,
      backgroundColor: '#fff',
      border: `1px solid ${BORDER}`,
      padding: mobile ? '12px' : '10px',
      boxShadow: CARD_SHADOW,
    }}
  >
    {children}
  </div>
);

export const FilterField = ({
  label,
  icon,
  children,
  fluid,
  as = 'label',
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  /** Take the full row (mobile) instead of hugging the content (desktop). */
  fluid?: boolean;
  /** `div` for controls with their own popover: a label would re-focus the input on every click. */
  as?: 'label' | 'div';
}) =>
  React.createElement(
    as,
    {
      style: {
        display: fluid ? 'flex' : 'inline-flex',
        width: fluid ? '100%' : undefined,
        boxSizing: 'border-box',
        alignItems: 'center',
        gap: 8,
        borderRadius: 12,
        backgroundColor: BG,
        border: `1px solid ${BORDER}`,
        padding: '8px 12px',
        minWidth: 0,
      },
    },
    <span style={{ color: MUTED, flexShrink: 0, display: 'flex' }}>{icon}</span>,
    <span
      style={{
        fontSize: '11px',
        color: MUTED,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        flexShrink: 0,
      }}
    >
      {label}
    </span>,
    children,
  );

/** The bare control that sits inside a FilterField: the pill is the border. */
export const filterControlStyle = (fluid?: boolean): React.CSSProperties => ({
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 500,
  color: TEAL_DARK,
  minWidth: '128px',
  flex: fluid ? 1 : undefined,
  width: fluid ? '100%' : undefined,
});

/** Segmented control (date presets, X/Z, XLSX/PDF). On mobile the options share the row and wrap when they must. */
export const FilterSegments = <T extends string>({
  options,
  value,
  onChange,
  fluid,
  ariaLabel,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  fluid?: boolean;
  ariaLabel: string;
}) => {
  const hasValue = options.some((option) => option.id === value);
  const move = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = options.length - 1;
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown' ? (index === last ? 0 : index + 1)
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? (index === 0 ? last : index - 1)
      : event.key === 'Home' ? 0
      : event.key === 'End' ? last
      : -1;
    if (next < 0) return;
    event.preventDefault();
    onChange(options[next].id);
    (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: fluid ? 'flex' : 'inline-flex',
        flexWrap: fluid ? 'wrap' : undefined,
        width: fluid ? '100%' : undefined,
        alignItems: 'center',
        borderRadius: 12,
        backgroundColor: BG,
        padding: 4,
      }}
    >
      {options.map((option, index) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active || (!hasValue && index === 0) ? 0 : -1}
            onKeyDown={(event) => move(event, index)}
            onClick={() => onChange(option.id)}
            style={{
              padding: fluid ? '8px 10px' : '8px 14px',
              borderRadius: 8,
              border: 'none',
              fontFamily: 'inherit',
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor: active ? '#fff' : 'transparent',
              color: active ? TEAL_DARK : MUTED,
              boxShadow: active ? '0 1px 3px rgba(5,41,42,0.08)' : 'none',
              flex: fluid ? '1 1 auto' : undefined,
              textAlign: 'center',
              whiteSpace: fluid ? 'nowrap' : undefined,
              cursor: 'pointer',
              transition: 'background-color 120ms ease, color 120ms ease',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

/** The dark primary action next to the filters (Refresh, Load, Generate, Export). */
export const FilterAction = ({
  label,
  onClick,
  disabled,
  busy,
  icon,
  fluid,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Keeps the button visible but inert while a request is in flight. */
  busy?: boolean;
  icon?: React.ReactNode;
  fluid?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || busy}
    style={{
      display: fluid ? 'flex' : 'inline-flex',
      width: fluid ? '100%' : undefined,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      borderRadius: 12,
      border: 'none',
      padding: '8px 16px',
      backgroundColor: disabled ? '#9CA3AF' : TEAL_DARK,
      color: '#fff',
      fontFamily: 'inherit',
      fontSize: '13px',
      fontWeight: 600,
      opacity: busy ? 0.7 : 1,
      cursor: busy ? 'wait' : disabled ? 'not-allowed' : 'pointer',
      boxShadow: disabled ? 'none' : '0 6px 16px rgba(5,41,42,0.18)',
      transition: 'opacity 120ms ease',
    }}
  >
    {icon}
    {label}
  </button>
);

export const IconCalendar = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const IconPin = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M12 22s7-6.4 7-12a7 7 0 1 0-14 0c0 5.6 7 12 7 12z" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

export const IconTag = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="8" cy="8" r="1.6" fill="currentColor" />
  </svg>
);

export const IconReceipt = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const IconRefresh = ({ spinning }: { spinning?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: spinning ? 'filter-spin 1s linear infinite' : undefined }}
  >
    <path d="M21 12a9 9 0 1 1-3-6.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M21 4v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <style>{`@keyframes filter-spin { to { transform: rotate(360deg); } }`}</style>
  </svg>
);

export const IconLayers = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M12 4l8 4-8 4-8-4 8-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M4 12l8 4 8-4M4 16l8 4 8-4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
